import { getShellLogger } from '../../common/logger'
import { getToken } from './token'
import { refresh } from './refresh'
import { HttpResponseError } from '../../http-utils'
import type { RetryableRequestInit } from '../../common/retry'
import { fetchRetry, retry } from '../../common/retry'
import type { Token } from '@getgo/auth-client'

export interface TypedResponse<T = unknown> extends Response {
  /**
   * this will override `json` method from `Body` that is extended by `Response`
   * interface Body {
   *     json(): Promise<any>;
   * }
   */
  json<P = T>(): Promise<P>
}

const retryGetToken = async () => {
  const maxNbTries = 2
  let token = getToken()
  let nbOfTries = 0

  while (!token && nbOfTries < maxNbTries) {
    nbOfTries++
    getShellLogger().error('Token not found or invalid')
    // Reminder: calling authenticatedFetch before the token is set will resolve the promise
    // to undefined, and not the expected typed object
    await refresh()
    token = getToken()
  }
  return token
}

const fetchAuth = async (token: Token, input: RequestInfo, init?: RetryableRequestInit): Promise<Response> => {
  const { token_type, access_token } = token
  const headers = new Headers(init?.headers)
  headers.set('Authorization', `${token_type} ${access_token}`)
  return await fetchRetry(input, {
    credentials: 'include',
    ...init,
    headers,
  })
}

export const authenticatedFetch = async <T = unknown>(
  input: RequestInfo,
  init?: RetryableRequestInit,
): Promise<TypedResponse<T>> => {
  const token = await retryGetToken()
  if (!token || !token.access_token || !token.token_type) {
    throw new Error('Could not fetch the token')
  }

  const response = await fetchAuth(token, input, init)
  const { body, status, statusText } = response
  // Authenticated Fetch failed, so redirect to login
  if (status === 401) {
    getShellLogger().error('Authenticated Fetch failed')
    await refresh()
  } else if (status >= 400) {
    throw new HttpResponseError({ message: `${status}: ${statusText}`, response: undefined, status, statusText, body })
  }

  return response
}

/**
 * Returns a response based on authenticated fetch with randomized delays with a baseline.
 * This is to avoid DDOSing then back-end when we are trying to reconnect to the websocket.
 *
 * @param input - The request info, usually containing the info for the websocket
 * @param init - The request initiation that for fetch retries
 * @param retryInterval - The baseline interval, default is 250. This interval is randomized and added to the baseline
 * @returns The response or throws an error if the number of retries runs out
 */

export const authenticatedFetchWithRetries = async <T = unknown>(
  input: RequestInfo,
  init?: RetryableRequestInit,
  retryInterval = 250,
  maxRetry = 2,
): Promise<TypedResponse<T>> =>
  await retry(() => authenticatedFetch(input, init), {
    maxRetryNumber: maxRetry,
    retryAfterInMilliseconds: retryInterval,
    randomized: true,
    exponential: true,
  })
